import appConfig from 'app-config'
import actions from 'redux/actions'
import { getNavFromStructure } from 'common/helpers/getters'
import { constants, links, mixpanel, snowplow } from 'common/helpers'
import messages from './messages'

// eslint-disable-next-line
// import iconUpgrade from 'components/buttons/UpgradeButton/images/icon-upgrade-white.svg'
import iconLightning from '../images/lightning.svg'
import iconGift from '../images/gift.svg'


const getUpgradeMenuItem = ({ placement }) => ({
  title: messages.upgradeYourPlan,
  emojiIcon: '💰',
  dataTestId: 'upgradeYourPlan',
  onClick: () => {
    actions.models.ui.closeSideMenu()
    snowplow.track(constants.snowplowEvents.upgradeSubscriptionClick, { placement: 'hamburger menu' }, false)
    actions.models.subscription.handleUpgradeClick({ placement })
  },
})

const getPrepaidMenuItem = () => ({
  title: messages.prepaidPlans,
  emojiIcon: '🔥 ',
  to: links.upgradePrepaid,
  dataTestId: 'prepaidPlans',
})

const getProfileNavItem = ({ subscription, logoutNavItem, isPrepaidMenuItemVisible }) => {
  const { isUndeliverable, isCancelled, isUpgradable, isPaused } = subscription

  const profileSubNav = [{
    title: messages.subscriptionStatus,
    to: links.profile.ordersStatus,
    dataTestId: 'subscriptionStatus',
  }]

  if (isCancelled) {
    profileSubNav.push({
      title: messages.resubscribe,
      dataTestId: 'resubscribe',
      onClick: () => {
        const resubscribeCode = constants.resubscribeTypes.resubnew

        snowplow.track(constants.snowplowEvents.resubscribeClick, {
          placement: 'hamburger menu',
        })

        if (isUndeliverable) {
          actions.models.router.push(`${links.subscribe}?resubscribeCode=${resubscribeCode}`)
        }
        else {
          actions.models.subscription.resubscribe({ resubscribeCode, popup: 'hamburger menu' })
            .then(({ premiumProduct }) => {
              if (premiumProduct) {
                actions.models.modals.open(constants.modals.premiumProducts.WelcomeBack, {
                  item: premiumProduct,
                })
              }
              else {
                actions.models.modals.open(constants.modals.resubscribe.ResubscribeSuccess)
              }
            }, () => {})
        }
      },
    })
  }

  profileSubNav.push(
    { title: messages.sendGift, to: links.gift, dataTestId: 'sendGift' },
    { title: messages.inviteFriends, to: links.invite, dataTestId: 'inviteFriends' },
  )

  if (isUpgradable) {
    profileSubNav.push(getUpgradeMenuItem({ placement: 'mobile menu dropdown in profile' }))
  }

  if (isPrepaidMenuItemVisible) {
    profileSubNav.push(getPrepaidMenuItem())
  }

  if (isPaused) {
    profileSubNav.push({
      title: messages.unpause,
      dataTestId: 'unpause',
      onClick: () => {
        snowplow.track(constants.snowplowEvents.unpauseClick, { placement: 'hamburger menu' }, false)
        actions.models.subscription.unpause({ placement: 'hamburger menu' })
      },
    })
  }

  profileSubNav.push(
    { title: messages.retakeQuiz,           to: links.quiz, dataTestId: 'quiz' },
    { title: messages.personalInfo,         to: links.profile.personal, dataTestId: 'personal' },
    { title: messages.billingAndShipping,   to: links.profile.billingAndShipping, dataTestId: 'billingAndShipping' },
    { title: messages.changePassword,       to: links.profile.password, dataTestId: 'password' },
    logoutNavItem,
  )

  return {
    title: messages.profile,
    dataTestId: 'profile',
    subNav: profileSubNav,
  }
}

const getAuthNav = ({ structure, subscription, globals }) => {
  const { isUpgradableForMonthlyPlan, isCancelled: isSubscriptionCancelled, isUpgradable } = subscription

  const {
    isCollectionsEnabled,
    isIncubatorNeonHeatEnabled,
    resubscribeBurgerMenuAbValue,
    isResubscribeMembershipPageEnabled,
    isFatherDayBambooGiftSetEnabled,
    isPrepaidPlansLandingPageEnabled,
    isPopularInStatesHidden,
    isFragranceFinderHidden,
    isFragranceHoroscopeHidden,
    isGiftsHidden,
    isSeparateWellnessEnabled,
  } = globals

  const isNewSkincareStructureEnabled = Boolean(structure && structure.skincare)
  const isSeparateWellnessVisible = Boolean(isSeparateWellnessEnabled && structure && structure.wellness)

  const isPrepaidMenuItemVisible = (
    isPrepaidPlansLandingPageEnabled
    && isUpgradableForMonthlyPlan
  )

  const logoutNavItem = {
    title: messages.logout,
    toOLD: links.logout,
    id: 'logout',
    onClick: (event) => {
      mixpanel.reset()
      actions.models.me.removeStorageData()

      if (process.env.LOCALTEST || process.env.WEBPACK === 'dev') {
        if (event) {
          event.preventDefault()
        }
        window.open(`${appConfig.services.api}logoff`)

        setTimeout(() => {
          window.location.pathname = links.login
        }, 500)
      }
    },
  }

  const nav = []

  if (isResubscribeMembershipPageEnabled && isSubscriptionCancelled) {
    if (resubscribeBurgerMenuAbValue === 'a') {
      nav.push({
        title: messages.resubscribe,
        to: links.resubscribe,
        brandColor: true,
        onClick: () => mixpanel.track(constants.analyticsEvents.resubscribeButton, {
          placement: 'burger menu',
        }),
        dataTestId: 'resubscribe',
      })
    }
    else if (resubscribeBurgerMenuAbValue === 'b') {
      nav.push({
        title: messages.whatIsNew,
        to: links.membership,
        icon: iconGift,
        brandColor: true,
        onClick: () => mixpanel.track(constants.analyticsEvents.membershipButtonClick, {
          placement: 'burger menu',
        }),
        dataTestId: 'whatIsNew',
      })
    }
    else if (resubscribeBurgerMenuAbValue === 'c') {
      nav.push({
        title: messages.reasonsToResubscribe,
        to: links.membership,
        isBanner: true,
        brandColor: true,
        onClick: () => mixpanel.track(constants.analyticsEvents.membershipButtonClick, {
          placement: 'burger menu',
        }),
        dataTestId: 'reasonsToResubscribe',
      })
    }
  }
  else if (isIncubatorNeonHeatEnabled) {
    nav.push({
      title: messages.rateNeonHeatKit,
      to: links.incubator.neonHeat,
      icon: iconLightning,
      isBanner: true,
      dataTestId: 'rateNeonHeatKit',
    })
  }
  else {
    nav.push({
      title: messages.earnFreeFragrance,
      to: links.invite,
      icon: iconGift,
      onClick: () => mixpanel.track(constants.analyticsEvents.shareLinkClick, {
        placement: 'mobile menu dropdown',
      }),
      dataTestId: 'earnFreeFragrance',
    })
  }

  nav.push(
    { title: messages.myQueue, to: links.queue, dataTestId: 'queue' },
    { title: messages.tracking, to: links.tracking, dataTestId: 'tracking' },
    getProfileNavItem({ subscription, logoutNavItem, isPrepaidMenuItemVisible }),
    {
      title: messages.main,
      dataTestId: constants.testIds.headerSubNav.main,
      subNav: [
        { title: messages.men, to: links.subscription.mainMen, dataTestId: constants.testIds.headerSubNav.men },
        { title: messages.women, to: links.subscription.mainWomen, dataTestId: constants.testIds.headerSubNav.women },
      ],
    }
  )

  nav.push({
    title: messages.fineFragrance,
    dataTestId: constants.testIds.headerSubNav.fineFragrance,
    subNav: (() => {
      const subNav = [
        { title: messages.perfumes, to: links.subscription.perfumes, dataTestId: constants.testIds.headerSubNav.perfumes },
        { title: messages.colognes, to: links.subscription.colognes, dataTestId: constants.testIds.headerSubNav.colognes },
        { title: messages.recommendations, to: links.subscription.recommendations, dataTestId: constants.testIds.headerSubNav.recommendations },
      ]

      if (!isPopularInStatesHidden) {
        subNav.push({ title: messages.fragranceByState, to: links.popularInStates, dataTestId: 'fragranceByState' })
      }

      if (isCollectionsEnabled) {
        subNav.push(
          {
            title: messages.collectionsWomen,
            to: links.subscription.collectionsWomen,
            dataTestId: constants.testIds.headerSubNav.collectionsWomen,
            onClick: () => {
              actions.misc.cookies.setSessionItem(constants.cookieNames.collectionsClickPlacement, 'mobile navigation')
            },
          },
          {
            title: messages.collectionsMen,
            to: links.subscription.collectionsMen,
            dataTestId: constants.testIds.headerSubNav.collectionsMen,
            onClick: () => {
              actions.misc.cookies.setSessionItem(constants.cookieNames.collectionsClickPlacement, 'mobile navigation')
            },
          },
        )
      }

      if (!isFragranceHoroscopeHidden) {
        subNav.push({ title: messages.fragranceHoroscope, to: links.horoscope, dataTestId: constants.testIds.headerSubNav.horoscope })
      }

      subNav.push(
        { title: messages.perfumeOfTheMonth, to: links.subscription.currPerfumeOfTheMonth, dataTestId: 'perfumeOfTheMonth' },
        { title: messages.cologneOfTheMonth, to: links.subscription.currCologneOfTheMonth, dataTestId: 'cologneOfTheMonth' },
        { title: messages.extras, to: links.subscription.extras, dataTestId: constants.testIds.headerSubNav.extras },
      )

      if (!isFragranceFinderHidden) {
        subNav.push({ title: messages.fragranceFinder, to: links.fragranceFinder, dataTestId: constants.testIds.headerSubNav.fragranceFinder })
      }

      return subNav
    })(),
  })

  nav.push(
    // MAKEUP
    {
      title: messages.makeup,
      dataTestId: constants.testIds.headerSubNav.makeup,
      subNav: [
        { title: messages.allMakeup, to: links.makeup,         dataTestId: constants.testIds.headerSubNav.allMakeup },
        { title: messages.palettes,  to: links.makeupPalettes, dataTestId: constants.testIds.headerSubNav.palettes },
        { title: messages.face,      to: links.makeupFace,      dataTestId: constants.testIds.headerSubNav.face },
        { title: messages.cheek,     to: links.makeupCheek,      dataTestId: constants.testIds.headerSubNav.cheek },
        { title: messages.eye,       to: links.makeupEye,     dataTestId: constants.testIds.headerSubNav.eye },
        { title: messages.lip,       to: links.makeupLip,    dataTestId: constants.testIds.headerSubNav.lip },
        { title: messages.tools,     to: links.makeupTools,    dataTestId: constants.testIds.headerSubNav.tools },
      ],
    },
    // SKINCARE
    {
      title: messages.skincare,
      dataTestId: 'skincare',
      subNav: isNewSkincareStructureEnabled
        ? getNavFromStructure({ structure: structure.skincare, root: 'skincare', isMobile: true })
        : [
          { title: messages.allSkincare, to: links.skincare, dataTestId: constants.testIds.headerSubNav.allSkincare },
          { title: messages.collectionsForWomen, to: links.skincareCollectionWomen, dataTestId: constants.testIds.headerSubNav.collectionsForWomen },
          { title: messages.collectionsForMen, to: links.skincareCollectionMen, dataTestId: constants.testIds.headerSubNav.collectionsForMen },
          { title: messages.bodyLip, to: links.skincareLip, dataTestId: 'skincareLip' },
          { title: messages.eye, to: links.skincareEye, dataTestId: constants.testIds.headerSubNav.skincareEye },
          { title: messages.cleansers, to: links.skincareCleansers, dataTestId: constants.testIds.headerSubNav.cleansers },
          { title: messages.masks, to: links.skincareMasks, dataTestId: 'skincareMasks' },
          { title: messages.moisturizers, to: links.skincareMoisturizers, dataTestId: 'skincareMoisturizers' },
          { title: messages.treatments, to: links.skincareTreatments, dataTestId: 'skincareTreatments' },
          { title: messages.mens, to: links.skincareMens, dataTestId: 'skincareMens' },
          { title: messages.wellness, to: links.skincareWellness, dataTestId: 'skincareWellness' },
        ],
    },
  )

  if (isSeparateWellnessVisible) {
    nav.push({
      title: messages.wellness,
      dataTestId: 'wellness',
      subNav: getNavFromStructure({ structure: structure.wellness, root: 'wellness', isMobile: true }),
    })
  }

  nav.push(
    // PERSONAL CARE
    {
      title: messages.personalCare,
      dataTestId: 'personalCare',
      subNav: [
        { title: messages.allPersonalCare, to: links.personalCare, dataTestId: 'personalCare' },
        { title: messages.bodyMoisturizers, to: links.personalCareBodyMoisturizers, dataTestId: 'personalCareBodyMoisturizers' },
        { title: messages.shower, to: links.personalCareShower, dataTestId: 'personalCareShower' },
        { title: messages.handCreams, to: links.personalCareHandCreams, dataTestId: 'personalCareHandCreams' },
      ],
    },
    // CANDLES
    {
      title: messages.candles,
      dataTestId: constants.testIds.headerSubNav.candles,
      subNav: [
        { title: messages.scentedCandles, to: links.candles, dataTestId: constants.testIds.headerSubNav.candles },
      ],
    },
    // GIFTS
    {
      title: messages.gifts,
      dataTestId: constants.testIds.headerSubNav.gifts,
      subNav: [
        ...(
          isFatherDayBambooGiftSetEnabled ? [
            { title: messages.fathersDay, to: links.fathersDayGiftSet, dataTestId: 'fatherDayGiftSet' },
          ] : []
        ),
        { title: messages.allGifts, to: links.gifts, dataTestId: constants.testIds.headerSubNav.gifts },
        { title: messages.giftCard, to: links.giftCard, dataTestId: constants.testIds.headerSubNav.giftCard },
        ...(
          !isGiftsHidden ? [
            { title: messages.giftSetsMen, to: links.giftSetsMen, dataTestId: constants.testIds.headerSubNav.giftSetsMen },
            { title: messages.giftSetsWomen, to: links.giftSet, dataTestId: constants.testIds.headerSubNav.giftSet },
          ] : []
        ),
        { title: messages.skincareSets, to: links.skincareSets, dataTestId: constants.testIds.headerSubNav.skincareSets },
        { title: messages.giftSubscription, toOLD: links.gift, dataTestId: constants.testIds.headerSubNav.giftSubscription },
      ],
    }
  )

  if (isUpgradable) {
    nav.push(getUpgradeMenuItem({ placement: 'mobile menu dropdown' }))
  }

  if (isPrepaidMenuItemVisible) {
    nav.push(getPrepaidMenuItem())
  }

  nav.push({
    title: messages.help,
    redirect: links.faq,
    id: 'faq',
    onClick: () => mixpanel.track(constants.analyticsEvents.faqLinkClick, { placement: 'header' }),
    dataTestId: 'help',
  })

  nav.push(logoutNavItem)

  return nav
}


export default getAuthNav
